/**
 * Login Screen Component
 *
 * Split-screen layout: left branding panel + right auth form
 * Mobile-first: branding panel hidden on small screens
 */

import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logoImage from "../assets/svg/logo.svg";
import logoDark from "../assets/svg/logo_dark.svg";
import GoogleOAuthService from "../services/auth/google-oauth";
import M365OAuthService from "../services/auth/m365-oauth";
import { useAuth } from "../contexts/AuthContext";

/* ------------------------------------------------------------------ */
/*  Inline SVG icons for OAuth buttons & feature badges               */
/* ------------------------------------------------------------------ */

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
      fill="#4285F4"
    />
    <path
      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
      fill="#34A853"
    />
    <path
      d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"
      fill="#FBBC05"
    />
    <path
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z"
      fill="#EA4335"
    />
  </svg>
);

const MicrosoftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const GitHubIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12Z" />
  </svg>
);

const LinkedInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286ZM5.337 7.433a2.062 2.062 0 1 1 0-4.125 2.062 2.062 0 0 1 0 4.125ZM6.84 20.452H3.834V9H6.84v11.452ZM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003Z" />
  </svg>
);

const ScreenAwareIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const ContextDrivenIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

const ToolIntegratedIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76Z" />
  </svg>
);

const AutonomousReadyIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const SparkleIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function LoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Redirect to intended destination if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || "/chat";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, location]);

  /* ---------- OAuth handlers ---------- */

  const handleGoogleSignIn = async () => {
    try {
      await GoogleOAuthService.initiateLogin();
    } catch (error) {
      console.error("Google sign in error:", error);
      alert("Failed to initiate Google sign in. Please try again.");
    }
  };

  const handleMicrosoftSignIn = async () => {
    try {
      await M365OAuthService.initiateLogin();
    } catch (error) {
      console.error("Microsoft sign in error:", error);
      alert("Failed to initiate Microsoft sign in. Please try again.");
    }
  };

  const handleGitHubSignIn = () => {
    alert("GitHub sign-in coming soon!");
  };

  const handleLinkedInSignIn = () => {
    alert("LinkedIn sign-in coming soon!");
  };

  /* ---------- Email/password handler ---------- */

  const handleEmailSignUp = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !confirmPassword) {
      alert("Please fill in all fields.");
      return;
    }
    if (password !== confirmPassword) {
      alert("Passwords do not match.");
      return;
    }
    alert("Email sign-up coming soon! Please use an OAuth provider for now.");
  };

  /* ---------- Loading state ---------- */

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#FAF6F1] dark:bg-[#1A2D47]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4915A] mx-auto mb-4" />
          <p className="text-[#5B5B5B] dark:text-[#A0AEC0]">Loading...</p>
        </div>
      </div>
    );
  }

  /* ---------- Render ---------- */

  return (
    <div className="min-h-screen flex flex-col lg:flex-row overflow-auto bg-[#FAF6F1] dark:bg-[#1A2D47]">
      {/* ======================== LEFT PANEL (branding) ======================== */}
      <div
        className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-10"
        style={{
          background:
            "linear-gradient(180deg, #1E2D42 0%, #2A3547 25%, #3D2E1D 55%, #6B4A2E 80%, #8B6440 100%)",
        }}
      >
        {/* Glass card */}
        <div className="relative max-w-lg w-full rounded-2xl border border-[#A08B6F]/25 bg-white/5 backdrop-blur-md p-10 text-center">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="w-16 h-16 rounded-xl bg-[#3A3A3A]/70 flex items-center justify-center shadow-lg border border-white/10">
              <img
                src={logoImage}
                alt="DuneWorks"
                className="w-10 h-10 dark:hidden"
              />
              <img
                src={logoDark}
                alt="DuneWorks"
                className="w-10 h-10 hidden dark:block"
              />
            </div>
          </div>

          {/* Tagline */}
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#D4915A] mb-6">
            Where Capability Becomes Productivity
          </p>

          {/* Heading */}
          <h1 className="text-4xl xl:text-5xl font-bold leading-tight text-white mb-6">
            Your <span className="italic text-[#D4915A]">Autonomous</span>
            <br />
            Engineering Twin
          </h1>

          {/* Subtitle */}
          <p className="text-[#B0B8C4] text-base leading-relaxed mb-8">
            Always watching. Always learning.
            <br />
            Works inside your tools. Acts when you need it.
          </p>

          {/* Feature badges */}
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { icon: <ScreenAwareIcon />, label: "Screen-Aware" },
              { icon: <ContextDrivenIcon />, label: "Context-Driven" },
              { icon: <ToolIntegratedIcon />, label: "Tool-Integrated" },
              { icon: <AutonomousReadyIcon />, label: "Autonomous-Ready" },
            ].map((badge) => (
              <span
                key={badge.label}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-[#D4C5B3] bg-white/8 border border-[#A08B6F]/20"
              >
                {badge.icon}
                {badge.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ======================== RIGHT PANEL (auth form) ======================== */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 lg:py-0 lg:w-1/2 bg-[#FAF6F1] dark:bg-[#1A2D47]">
        <div className="w-full max-w-md p-0 lg:bg-white lg:dark:bg-[#1F3352] lg:rounded-2xl lg:p-8 lg:shadow-sm lg:border lg:border-[#EDE5D8] lg:dark:border-[#2C4060]">
          {/* Mobile-only logo */}
          <div className="flex justify-center mb-6 lg:hidden">
            <div className="w-14 h-14 rounded-xl bg-[#3A3A3A]/10 dark:bg-white/10 flex items-center justify-center">
              <img
                src={logoImage}
                alt="DuneWorks"
                className="w-9 h-9 dark:hidden"
              />
              <img
                src={logoDark}
                alt="DuneWorks"
                className="w-9 h-9 hidden dark:block"
              />
            </div>
          </div>

          {/* Heading */}
          <h2 className="text-2xl font-bold text-center text-[#1D1B19] dark:text-white mb-1">
            Activate Your Twin
          </h2>
          <p className="text-sm text-center text-[#6B6B6B] dark:text-[#A0AEC0] mb-8">
            Choose how you want to get started
          </p>

          {/* OAuth buttons – stacked vertically */}
          <div className="flex flex-col gap-3 mb-6 w-full">
            <button
              onClick={handleGoogleSignIn}
              className="w-full px-2 h-12 bg-[#1A2D47] dark:bg-[#2C4060] hover:bg-[#243854] dark:hover:bg-[#374D6D] active:scale-[0.97] rounded-lg flex items-center justify-center gap-2 transition-all duration-150 touch-manipulation border border-transparent dark:border-[#455D7D]"
            >
              <GoogleIcon />
              <span className="text-sm font-medium text-white">
                Continue with Google
              </span>
            </button>

            <div className="flex items-center gap-3 w-full">
              <div className="flex-1 h-px bg-[#D7C7B5] dark:bg-[#455D7D]" />
              <span className="text-xs font-semibold text-[#6B6B6B] dark:text-[#A0AEC0] tracking-wide">
                or
              </span>
              <div className="flex-1 h-px bg-[#D7C7B5] dark:bg-[#455D7D]" />
            </div>

            <button
              onClick={handleMicrosoftSignIn}
              className="w-full px-2 h-12 bg-[#1A2D47] dark:bg-[#2C4060] hover:bg-[#243854] dark:hover:bg-[#374D6D] active:scale-[0.97] rounded-lg flex items-center justify-center gap-2 transition-all duration-150 touch-manipulation border border-transparent dark:border-[#455D7D]"
            >
              <MicrosoftIcon />
              <span className="text-sm font-medium text-white">
                Continue with Microsoft
              </span>
            </button>
          </div>

          {/* OR divider */}
          {/* <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-[#D7C7B5] dark:bg-[#455D7D]" />
            <span className="text-xs font-semibold text-[#6B6B6B] dark:text-[#A0AEC0] tracking-wide">
              OR
            </span>
            <div className="flex-1 h-px bg-[#D7C7B5] dark:bg-[#455D7D]" />
          </div> */}

          {/* Email/password form */}

          {/* Terms */}
          <p className="mt-6 text-center text-xs text-[#6B6B6B] dark:text-[#8B9DB5]">
            By signing up you agree to the{" "}
            <a
              href="#"
              className="underline hover:text-[#1D1B19] dark:hover:text-white"
            >
              Terms
            </a>{" "}
            and{" "}
            <a
              href="#"
              className="underline hover:text-[#1D1B19] dark:hover:text-white"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
